'use client';

import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

// ─── Types ────────────────────────────────────────────────────────────────────

interface QuickActionsFabProps {
  onCreateJob?: () => void;
  onAddMaterialOrder?: () => void;
  onScheduleInspection?: () => void;
  onLogIncident?: () => void;
  onSendBroadcast?: () => void;
}

type ActionKey = 'create_job' | 'add_material_order' | 'schedule_inspection' | 'log_incident' | 'send_broadcast';

interface ActionDef {
  key: ActionKey;
  label: string;
  color: string;
  iconPath: React.ReactNode;
}

// ─── Action Definitions ───────────────────────────────────────────────────────

const ACTIONS: ActionDef[] = [
  {
    key: 'create_job',
    label: 'Create Job',
    color: '#E89A3C',
    iconPath: (
      <>
        <rect x="3" y="6" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M12 11v6M9 14h6" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  {
    key: 'add_material_order',
    label: 'Add Material Order',
    color: '#F5A623',
    iconPath: (
      <>
        <path d="M3 3h2l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
        <path d="M12 10v4M10 12h4" strokeLinecap="round" />
      </>
    ),
  },
  {
    key: 'schedule_inspection',
    label: 'Schedule Inspection',
    color: '#16a34a',
    iconPath: (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  {
    key: 'log_incident',
    label: 'Log Incident',
    color: '#dc2626',
    iconPath: (
      <>
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
      </>
    ),
  },
  {
    key: 'send_broadcast',
    label: 'Send Broadcast',
    color: '#1C2E5E',
    iconPath: (
      <>
        <path d="M3 11l18-8-8 18-2-8-8-2z" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const QuickActionsFab = ({
  onCreateJob,
  onAddMaterialOrder,
  onScheduleInspection,
  onLogIncident,
  onSendBroadcast,
}: QuickActionsFabProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const firstActionRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Focus first action on open
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => firstActionRef.current?.focus());
    }
  }, [isOpen]);

  const handleAction = (action: ActionDef) => {
    switch (action.key) {
      case 'create_job':
        if (onCreateJob) return onCreateJob();
        toast('Coming soon — Create Job', { icon: '🔨' });
        return;
      case 'add_material_order':
        if (onAddMaterialOrder) return onAddMaterialOrder();
        toast('Coming soon — Add Material Order', { icon: '📦' });
        return;
      case 'schedule_inspection':
        if (onScheduleInspection) return onScheduleInspection();
        toast('Coming soon — Schedule Inspection', { icon: '📋' });
        return;
      case 'log_incident':
        if (onLogIncident) return onLogIncident();
        toast('Coming soon — Log Incident', { icon: '⚠️' });
        return;
      case 'send_broadcast':
        if (onSendBroadcast) return onSendBroadcast();
        toast('Coming soon — Send Broadcast', { icon: '📣' });
        return;
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed bottom-6 right-6 z-[2000] flex flex-col items-end gap-3"
      aria-label="Quick actions menu"
    >
      {/* Action items — render ABOVE the FAB */}
      <div
        id="quick-actions-menu"
        role="menu"
        className={`flex flex-col items-end gap-2.5 transition-all duration-300 ${
          isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        {ACTIONS.map((action, idx) => (
          <button
            key={action.key}
            type="button"
            role="menuitem"
            ref={idx === 0 ? firstActionRef : undefined}
            onClick={() => { handleAction(action); setIsOpen(false); }}
            className="group flex items-center gap-3 transition-all"
            style={{
              transitionDelay: isOpen ? `${idx * 40}ms` : '0ms',
              transform: isOpen ? 'translateY(0)' : 'translateY(12px)',
              opacity: isOpen ? 1 : 0,
            }}
          >
            {/* Label pill */}
            <span className="px-3 py-1.5 rounded-full text-[12px] font-semibold shadow-md bg-white text-gray-700 whitespace-nowrap group-hover:bg-gray-50 transition-colors">
              {action.label}
            </span>
            {/* Icon button */}
            <span
              className="w-11 h-11 rounded-full flex items-center justify-center shadow-md transition-all group-hover:scale-110"
              style={{ background: action.color }}
            >
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                {action.iconPath}
              </svg>
            </span>
          </button>
        ))}
      </div>

      {/* Main FAB button */}
      <button
        type="button"
        onClick={() => setIsOpen(p => !p)}
        className="relative w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300"
        style={{
          background: isOpen
            ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
            : 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)',
          boxShadow: isOpen
            ? '0 10px 30px rgba(220, 38, 38, 0.35)'
            : '0 10px 30px rgba(232, 154, 60, 0.35)',
          transform: isOpen ? 'rotate(135deg)' : 'rotate(0deg)',
        }}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close quick actions' : 'Open quick actions'}
        aria-controls="quick-actions-menu"
      >
        {/* Plus icon — rotates to become an X when isOpen */}
        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
};

export default QuickActionsFab;
